import {
  useGetInitiativeUniversalTrackersQuery,
  useOverrideAggregationMutation,
} from '@api/initiative-universal-trackers';
import { InitiativeUniversalTracker, AggregationOverrideData } from '@g17eco/types/initiativeUniversalTracker';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { getInitialAggregationOverrideData } from './utils';
import { useMemo } from 'react';
import { useAppSelector } from '@reducers/index';
import { getRootConfig } from '@selectors/globalData';
import { getRootOrg } from '@selectors/initiative';
import { FeaturePermissions } from '@services/permissions/FeaturePermissions';
import { AggregationOverrideForm } from './AggregationOverrideForm';
import { BulkActionUtr } from '@g17eco/types/survey-question-list';
import { shouldAutoOptInToWeightedAggregation } from '@utils/initiative';

interface Props {
  handleReload: (props?: {
    reloadSurvey?: boolean;
    closeModal?: boolean;
    scrollToTop?: boolean;
  }) => Promise<void | undefined>;
  selectedQuestion: BulkActionUtr | undefined;
  initiativeId: string;
  handleCloseModal: () => void;
}

export const AggregationOverrideContainer = (props: Props) => {
  const { handleReload, selectedQuestion, initiativeId, handleCloseModal } = props;
  const rootConfig = useAppSelector(getRootConfig);
  const rootOrg = useAppSelector(getRootOrg);
  const isAutoOptIn = shouldAutoOptInToWeightedAggregation(rootOrg?.created);
  const canAccessAggregationOverrides = FeaturePermissions.canAccessAggregationOverrides(rootConfig);
  const allowRender = canAccessAggregationOverrides && !!initiativeId && !!selectedQuestion;
  const { data: initiativeUtrs, isFetching } = useGetInitiativeUniversalTrackersQuery(initiativeId, {
    skip: !allowRender,
  });

  const initiativeUtrMap = useMemo(() => {
    return new Map<string, InitiativeUniversalTracker>(
      (initiativeUtrs ?? []).map((utr: InitiativeUniversalTracker) => [utr.universalTrackerId, utr]),
    );
  }, [initiativeUtrs]);

  const [updateAggregationOverride, { isLoading: isUpdating, error }] = useOverrideAggregationMutation();

  const isLoading = isFetching || isUpdating;

  if (!allowRender) {
    return null;
  }

  const initialData = getInitialAggregationOverrideData({ initiativeUtrMap, utr: selectedQuestion });

  const handleUpdateAggregationOverride = async ({ aggregationConfig, table, aggregationValueType }: AggregationOverrideData) => {
    updateAggregationOverride({
      aggregationConfig,
      table,
      initiativeId,
      utrId: selectedQuestion._id,
      aggregationValueType,
    })
      .unwrap()
      .then(() => {
        generateToast({ color: 'success', message: 'Aggregation overrides updated successfully' });
        handleReload({ reloadSurvey: true, closeModal: true });
      })
      .catch((error) => {
        toastFromError({ error, title: 'Failed to update aggregation overrides' });
        handleReload({ scrollToTop: true });
      });
  };

  return (
    <AggregationOverrideForm
      key={JSON.stringify({ initialData })}
      isLoading={isLoading}
      error={error}
      handleCloseModal={handleCloseModal}
      selectedQuestion={selectedQuestion}
      initialData={initialData}
      handleUpdateAggregationOverride={handleUpdateAggregationOverride}
      initiativeUtrMap={initiativeUtrMap}
      rootInitiativeId={initiativeId}
      isAutoOptIn={isAutoOptIn}
    />
  );
};
